<script setup>
import { ref, computed, provide, onMounted, watch } from 'vue';
import { useOnline } from '@vueuse/core';
import { dynamicTime } from 'shared/helpers/timeHelper';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useHaptics } from 'dashboard/composables/useHaptics';
import { useStaggeredEnter } from 'dashboard/composables/useStaggeredEnter';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { useUISettings } from 'dashboard/composables/useUISettings';

import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import ChatTypeTabs from 'dashboard/components/widgets/ChatTypeTabs.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import MobileConversationHeader from './MobileConversationHeader.vue';
import MobileSearchView from './MobileSearchView.vue';
import MobileFilterSheet from './MobileFilterSheet.vue';
import MobileConversationStatusSheet from './MobileConversationStatusSheet.vue';
import MobileSnoozeSheet from './MobileSnoozeSheet.vue';
import MobilePullToRefresh from './MobilePullToRefresh.vue';
import MobileSwipeableRow from './MobileSwipeableRow.vue';

import wootConstants from 'dashboard/constants/globals';

const emit = defineEmits(['openConversation']);
const store = useStore();
const { t } = useI18n();
const { medium, success } = useHaptics();
const { beforeEnter, afterEnter, enterCancelled } = useStaggeredEnter();
const { checkMissingAttributes } = useConversationRequiredAttributes();
const { uiSettings, updateUISettings } = useUISettings();

// Pinned conversations live in the agent's ui_settings, so they follow the
// agent across devices. Most recently pinned first.
const PINNED_SETTINGS_KEY = 'mobile_pinned_conversation_ids';

const swipeOpenRowId = ref(null);
provide('swipeOpenRowId', swipeOpenRowId);

const listRef = ref(null);
const isPullRefreshing = ref(false);
const showFilterSheet = ref(false);
const showSearchView = ref(false);

// Shell offline: snapshot leve das últimas conversas para a lista abrir sem
// rede (render somente leitura + banner). O SW cobre HTML/assets; os dados
// vêm deste snapshot em localStorage.
const SNAPSHOT_KEY = 'chatwit_mobile_conversations_snapshot';
const isOnline = useOnline();
const showStatusSheet = ref(false);
const selectedConversation = ref(null);
const showSnoozeSheet = ref(false);
const snoozeTarget = ref(null);
const resolveAttributesModalRef = ref(null);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
// 0 = todas as inboxes (sem filtro)
const activeInboxId = ref(0);

const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('auth/getCurrentAccountId');
const activeInbox = useMapGetter('getSelectedInbox');
const inboxesList = useMapGetter('inboxes/getInboxes');
const conversationStats = useMapGetter('conversationStats/getStats');

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const chatLists = useMapGetter('getAllConversations');
const chatListLoadingStatus = useMapGetter('getChatListLoadingStatus');

const pinnedIds = computed(() => uiSettings.value[PINNED_SETTINGS_KEY] || []);
const isPinned = chat => pinnedIds.value.includes(chat.id);

const sortedChats = computed(() => {
  const pinned = pinnedIds.value
    .map(id => chatLists.value.find(chat => chat.id === id))
    .filter(Boolean);
  return [...pinned, ...chatLists.value.filter(chat => !isPinned(chat))];
});
const getConversationById = useMapGetter('getConversationById');
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  activeAssigneeTab
);

const chatListLoading = computed(() => {
  return (
    chatListLoadingStatus.value &&
    !chatLists.value.length &&
    !isPullRefreshing.value
  );
});

const listLoadingMore = computed(() => {
  return (
    chatListLoadingStatus.value &&
    chatLists.value.length > 0 &&
    !isPullRefreshing.value
  );
});

const allConversationsLoaded = computed(
  () => hasCurrentPageEndReached.value && !chatListLoading.value
);

const showInboxName = computed(() => {
  return !activeInbox.value && inboxesList.value.length > 1;
});

const conversationFilters = computed(() => ({
  page: currentPage.value + 1,
  assigneeType: activeAssigneeTab.value,
  status: activeStatus.value,
  sortBy: activeSortBy.value,
  inboxId: activeInboxId.value || undefined,
}));

const fetchConversations = ({ preserveRecords = false } = {}) => {
  store.dispatch('conversationPage/reset');

  if (!preserveRecords) {
    store.dispatch('emptyAllConversations');
  }

  // setActiveInbox keeps websocket-added conversations consistent with the
  // fetched scope (same guard desktop inbox views rely on).
  store.dispatch('setActiveInbox', activeInboxId.value || null);
  store.dispatch('setChatListFilters', {
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    page: 1,
    sortBy: activeSortBy.value,
    inboxId: activeInboxId.value || undefined,
  });
  return store.dispatch('fetchAllConversations');
};

const loadMoreConversations = () => {
  if (allConversationsLoaded.value || chatListLoadingStatus.value) return;

  store.dispatch('updateChatListFilters', {
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    page: currentPage.value + 1,
    sortBy: activeSortBy.value,
    inboxId: activeInboxId.value || undefined,
  });
  store.dispatch('fetchAllConversations');
};

const onAssigneeTabChange = tab => {
  activeAssigneeTab.value = tab;
  fetchConversations();
};

const onConversationClick = chat => {
  if (swipeOpenRowId.value) {
    swipeOpenRowId.value = null;
    return;
  }
  emit('openConversation', chat.id);
};

const getCurrentContact = chat => {
  const sender = chat.meta?.sender || {};
  if (!sender.id) return sender;

  const contact = store.getters['contacts/getContact'](sender.id);
  return Object.keys(contact).length ? contact : sender;
};

const getAssignee = chat => {
  return chat.meta?.assignee || {};
};

const getInbox = chat => {
  return chat.inbox_id ? store.getters['inboxes/getInbox'](chat.inbox_id) : {};
};

const closeStatusSheet = () => {
  showStatusSheet.value = false;
  selectedConversation.value = null;
};

const toggleConversationStatus = async (
  conversationId,
  status,
  snoozedUntil = null,
  customAttributes = null
) => {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  // Haptic fires at tap time: iOS drops the Taptic switch trick once the
  // user activation expires across an await.
  if (status === wootConstants.STATUS_TYPE.RESOLVED) {
    success();
  } else {
    medium();
  }
  await store.dispatch('toggleStatus', payload);
  useAlert(t('CONVERSATION.CHANGE_STATUS'));
};

const openStatusSheet = chat => {
  selectedConversation.value = chat;
  showStatusSheet.value = true;
};

const getSwipeActions = () => {
  return [
    {
      key: 'status',
      icon: 'i-lucide-sliders-horizontal',
      color: 'bg-n-teal-9',
      label: t('MOBILE.SWIPE.STATUS'),
    },
  ];
};

const getLeftSwipeActions = chat => {
  const hasUnread = chat.unread_count > 0;
  const pinned = isPinned(chat);
  return [
    {
      key: hasUnread ? 'markRead' : 'markUnread',
      icon: hasUnread ? 'i-lucide-mail-open' : 'i-lucide-mail',
      color: 'bg-n-blue-9',
      label: hasUnread
        ? t('MOBILE.SWIPE.MARK_READ')
        : t('MOBILE.SWIPE.MARK_UNREAD'),
    },
    {
      key: pinned ? 'unpin' : 'pin',
      icon: pinned ? 'i-lucide-pin-off' : 'i-lucide-pin',
      color: 'bg-n-slate-9',
      label: pinned ? t('MOBILE.SWIPE.UNPIN') : t('MOBILE.SWIPE.PIN'),
    },
  ];
};

const togglePin = chat => {
  const others = pinnedIds.value.filter(id => id !== chat.id);
  const pinned = isPinned(chat);
  updateUISettings({
    [PINNED_SETTINGS_KEY]: pinned ? others : [chat.id, ...others],
  });
  useAlert(pinned ? t('MOBILE.SWIPE.UNPINNED') : t('MOBILE.SWIPE.PINNED'));
};

const onSwipeAction = (chat, actionKey) => {
  medium();
  if (actionKey === 'status') {
    openStatusSheet(chat);
  }
};

const onLeftSwipeAction = async (chat, actionKey) => {
  medium();
  if (actionKey === 'pin' || actionKey === 'unpin') {
    togglePin(chat);
  } else if (actionKey === 'markUnread') {
    try {
      await store.dispatch('markMessagesUnread', { id: chat.id });
      useAlert(t('MOBILE.SWIPE.MARKED_UNREAD'));
    } catch {
      // error already surfaced by the store action
    }
  } else if (actionKey === 'markRead') {
    try {
      const marked = await store.dispatch('markMessagesRead', { id: chat.id });
      if (marked !== false) {
        useAlert(t('MOBILE.SWIPE.MARKED_READ'));
      }
    } catch {
      // error already surfaced by the store action
    }
  }
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (!context) return;

  const existingConversation = getConversationById.value(context.id);
  const currentCustomAttributes = existingConversation?.custom_attributes || {};
  const mergedAttributes = { ...currentCustomAttributes, ...attributes };

  toggleConversationStatus(
    context.id,
    wootConstants.STATUS_TYPE.RESOLVED,
    context.snoozedUntil,
    mergedAttributes
  );
};

const onSnoozeSelect = async ({ snoozedUntil }) => {
  const chat = snoozeTarget.value;
  showSnoozeSheet.value = false;
  snoozeTarget.value = null;
  if (!chat) return;
  await toggleConversationStatus(
    chat.id,
    wootConstants.STATUS_TYPE.SNOOZED,
    snoozedUntil
  );
};

const onStatusSelect = async status => {
  const currentConversation = selectedConversation.value;

  closeStatusSheet();

  if (!currentConversation) return;

  if (status === wootConstants.STATUS_TYPE.PENDING) {
    await toggleConversationStatus(currentConversation.id, status);
    return;
  }

  if (status === wootConstants.STATUS_TYPE.SNOOZED) {
    snoozeTarget.value = currentConversation;
    showSnoozeSheet.value = true;
    return;
  }

  if (status === wootConstants.STATUS_TYPE.RESOLVED) {
    const latestConversation =
      getConversationById.value(currentConversation.id) || currentConversation;
    const currentCustomAttributes = latestConversation.custom_attributes || {};
    const { hasMissing, missing } = checkMissingAttributes(
      currentCustomAttributes
    );

    if (hasMissing) {
      resolveAttributesModalRef.value?.open(missing, currentCustomAttributes, {
        id: latestConversation.id,
        snoozedUntil: latestConversation.snoozed_until,
      });
      return;
    }

    await toggleConversationStatus(latestConversation.id, status);
  }
};

const onRefreshStart = () => {
  isPullRefreshing.value = true;
};

const onRefreshEnd = () => {
  isPullRefreshing.value = false;
};

const onRefresh = () => fetchConversations({ preserveRecords: true });

const persistOfflineSnapshot = () => {
  if (!chatLists.value.length) return;
  const snapshot = chatLists.value.slice(0, 30).map(chat => ({
    id: chat.id,
    name: chat.meta?.sender?.name || '',
    preview:
      chat.last_non_activity_message?.content ||
      chat.messages?.[chat.messages.length - 1]?.content ||
      '',
    timestamp: chat.timestamp,
    status: chat.status,
  }));
  try {
    window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // snapshot é best-effort
  }
};

watch(
  () => chatLists.value.map(chat => `${chat.id}:${chat.timestamp}`).join(','),
  persistOfflineSnapshot
);

const offlineSnapshot = computed(() => {
  if (isOnline.value || chatLists.value.length) return [];
  try {
    return JSON.parse(window.localStorage.getItem(SNAPSHOT_KEY)) || [];
  } catch {
    return [];
  }
});

const onFilterApply = filters => {
  if (filters.status) activeStatus.value = filters.status;
  if (filters.assigneeType) activeAssigneeTab.value = filters.assigneeType;
  if (filters.sortBy) activeSortBy.value = filters.sortBy;
  if (filters.inboxId !== undefined) activeInboxId.value = filters.inboxId;
  showFilterSheet.value = false;
  fetchConversations();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('setChatListFilters', conversationFilters.value);
  fetchConversations();
  store.dispatch('conversationStats/get', conversationFilters.value);
});

watch(conversationFilters, newFilters => {
  store.dispatch('updateChatListFilters', newFilters);
  store.dispatch('conversationStats/get', newFilters);
});
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <MobileConversationHeader
      @open-filter="showFilterSheet = true"
      @open-search="showSearchView = true"
    />
    <div
      v-if="!isOnline"
      class="flex items-center justify-center gap-2 bg-n-amber-3 px-4 py-1.5 text-xs font-medium text-n-amber-11"
    >
      <span class="i-lucide-wifi-off size-3.5" />
      {{ t('MOBILE.OFFLINE.BANNER') }}
    </div>
    <ChatTypeTabs
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      class="px-2 flex-shrink-0"
      @chat-tab-change="onAssigneeTabChange"
    />
    <MobilePullToRefresh
      :refresh-action="onRefresh"
      @refresh-start="onRefreshStart"
      @refresh-end="onRefreshEnd"
    >
      <div
        ref="listRef"
        data-mobile-pull-scroll
        class="relative flex-1 overflow-y-auto overscroll-y-contain px-2"
      >
        <div
          v-if="chatListLoading"
          class="flex items-center justify-center py-8"
        >
          <Spinner class="text-n-brand" />
        </div>
        <div
          v-else-if="offlineSnapshot.length"
          class="flex flex-col gap-0.5 px-2"
        >
          <div
            v-for="item in offlineSnapshot"
            :key="item.id"
            class="rounded-lg bg-white dark:bg-n-background px-4 py-3 opacity-80"
          >
            <div class="flex items-center justify-between gap-2">
              <p class="truncate text-sm font-semibold text-n-slate-12">
                {{ item.name }}
              </p>
              <span class="shrink-0 text-xs text-n-slate-10">
                {{ dynamicTime(item.timestamp) }}
              </span>
            </div>
            <p class="mt-0.5 truncate text-sm text-n-slate-11">
              {{ item.preview }}
            </p>
          </div>
        </div>
        <div
          v-else-if="!chatLists.length"
          class="flex items-center justify-center py-8 text-sm text-n-slate-10"
        >
          {{ t('MOBILE.CONVERSATIONS.NO_CONVERSATIONS') }}
        </div>
        <template v-else>
          <TransitionGroup
            appear
            enter-from-class="opacity-0 translate-y-4 scale-[0.97]"
            enter-active-class="transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            move-class="transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            leave-active-class="transition-all duration-200 ease-in !absolute left-2 right-2"
            leave-to-class="opacity-0 scale-95"
            @before-enter="beforeEnter"
            @after-enter="afterEnter"
            @enter-cancelled="enterCancelled"
          >
            <MobileSwipeableRow
              v-for="chat in sortedChats"
              :key="chat.id"
              :row-id="chat.id"
              :actions="getSwipeActions(chat)"
              :left-actions="getLeftSwipeActions(chat)"
              full-swipe
              class="mb-0.5"
              @action="onSwipeAction(chat, $event)"
              @left-action="onLeftSwipeAction(chat, $event)"
            >
              <ConversationCard
                :chat="chat"
                :current-contact="getCurrentContact(chat)"
                :assignee="getAssignee(chat)"
                :inbox="getInbox(chat)"
                :show-assignee="
                  activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL
                "
                :show-inbox-name="showInboxName"
                class="rounded-lg"
                @click="onConversationClick(chat)"
              />
              <span
                v-if="isPinned(chat)"
                class="i-lucide-pin pointer-events-none absolute bottom-2.5 end-3 size-3.5 rotate-45 text-n-slate-10"
              />
            </MobileSwipeableRow>
          </TransitionGroup>
          <div v-if="listLoadingMore" class="flex justify-center py-4">
            <Spinner class="text-n-brand" />
          </div>
          <IntersectionObserver
            v-if="!allConversationsLoaded && !chatListLoadingStatus"
            :options="{ root: listRef, rootMargin: '100px 0px' }"
            @observed="loadMoreConversations"
          />
        </template>
      </div>
    </MobilePullToRefresh>
    <MobileSearchView
      v-if="showSearchView"
      @close="showSearchView = false"
      @open-conversation="emit('openConversation', $event)"
    />
    <MobileFilterSheet
      v-if="showFilterSheet"
      :status="activeStatus"
      :assignee-type="activeAssigneeTab"
      :sort-by="activeSortBy"
      :inbox-id="activeInboxId"
      @apply="onFilterApply"
      @close="showFilterSheet = false"
    />
    <MobileConversationStatusSheet
      :open="showStatusSheet"
      @close="closeStatusSheet"
      @select="onStatusSelect"
    />
    <MobileSnoozeSheet
      :open="showSnoozeSheet"
      @close="showSnoozeSheet = false"
      @select="onSnoozeSelect"
    />
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
